// src/data/locations.js

// Carriers that specialize in pharmaceutical cold chain logistics
// Each has different performance characteristics (we'll use this for realistic data)

export const CARRIERS = [
  {
    id: 'FEDEX-CC',
    name: 'FedEx Custom Critical',
    // Performance stats (used to generate realistic excursion rates)
    reliabilityScore: 0.92,  // 92% shipments without issues
    avgTransitDays: 1.5,
    specialization: 'Ultra-cold',
  },
  {
    id: 'UPS-TT',
    name: 'UPS Temperature True',
    reliabilityScore: 0.89,
    avgTransitDays: 1.8,
    specialization: 'Refrigerated',
  },
  {
    id: 'WORLD-COURIER',
    name: 'World Courier',
    reliabilityScore: 0.95,  // Premium service, highest reliability
    avgTransitDays: 2.0,
    specialization: 'Biologics',
  },
  {
    id: 'MARKEN',
    name: 'Marken',
    reliabilityScore: 0.91,
    avgTransitDays: 1.7,
    specialization: 'Clinical Trials',
  },
  {
    id: 'DHL-MEDICAL',
    name: 'DHL Medical Express',
    reliabilityScore: 0.87,  // Slightly lower — more budget-friendly
    avgTransitDays: 2.2,
    specialization: 'General Pharma',
  },
];

// Manufacturing facilities (origins)
export const ORIGINS = [
  {
    id: 'PFIZER-MI',
    name: 'Pfizer Kalamazoo',
    city: 'Kalamazoo',
    state: 'MI',
    lat: 42.2917,
    lng: -85.5872,
  },
  {
    id: 'MODERNA-MA',
    name: 'Moderna Norwood',
    city: 'Norwood',
    state: 'MA',
    lat: 42.1945,
    lng: -71.1995,
  },
  {
    id: 'SANOFI-PA',
    name: 'Sanofi Swiftwater',
    city: 'Swiftwater',
    state: 'PA',
    lat: 41.0834,
    lng: -75.3412,
  },
  {
    id: 'ABBVIE-IL',
    name: 'AbbVie North Chicago',
    city: 'North Chicago',
    state: 'IL',
    lat: 42.3256,
    lng: -87.8412,
  },
  {
    id: 'NOVO-NC',
    name: 'Novo Nordisk Clayton',
    city: 'Clayton',
    state: 'NC',
    lat: 35.6507,
    lng: -78.4594,
  },
  {
    id: 'CSL-PA',
    name: 'CSL Behring King of Prussia',
    city: 'King of Prussia',
    state: 'PA',
    lat: 40.0912,
    lng: -75.3890,
  },
];

// Healthcare facilities (destinations)
export const DESTINATIONS = [
  {
    id: 'MGH',
    name: 'Massachusetts General Hospital',
    city: 'Boston',
    state: 'MA',
    lat: 42.3631,
    lng: -71.0686,
    type: 'Hospital',
  },
  {
    id: 'CLEVELAND',
    name: 'Cleveland Clinic',
    city: 'Cleveland',
    state: 'OH',
    lat: 41.5034,
    lng: -81.6205,
    type: 'Hospital',
  },
  {
    id: 'MAYO',
    name: 'Mayo Clinic Rochester',
    city: 'Rochester',
    state: 'MN',
    lat: 44.0225,
    lng: -92.4670,
    type: 'Hospital',
  },
  {
    id: 'HOPKINS',
    name: 'Johns Hopkins Hospital',
    city: 'Baltimore',
    state: 'MD',
    lat: 39.2965,
    lng: -76.5927,
    type: 'Hospital',
  },
  {
    id: 'STANFORD',
    name: 'Stanford Medical Center',
    city: 'Stanford',
    state: 'CA',
    lat: 37.4346,
    lng: -122.1751,
    type: 'Hospital',
  },
  {
    id: 'UCLA',
    name: 'UCLA Medical Center',
    city: 'Los Angeles',
    state: 'CA',
    lat: 34.0665,
    lng: -118.4466,
    type: 'Hospital',
  },
  {
    id: 'SINAI',
    name: 'Mount Sinai Hospital',
    city: 'New York',
    state: 'NY',
    lat: 40.7903,
    lng: -73.9524,
    type: 'Hospital',
  },
  {
    id: 'CVS-DIST',
    name: 'CVS Distribution Center',
    city: 'Indianapolis',
    state: 'IN',
    lat: 39.7684,
    lng: -86.1581,
    type: 'Distributor',
  },
  {
    id: 'MCKESSON',
    name: 'McKesson Distribution',
    city: 'Memphis',
    state: 'TN',
    lat: 35.1495,
    lng: -90.0490,
    type: 'Distributor',
  },
  {
    id: 'CARDINAL',
    name: 'Cardinal Health Hub',
    city: 'Columbus',
    state: 'OH',
    lat: 39.9612,
    lng: -82.9988,
    type: 'Distributor',
  },
];

// Root causes for temperature excursions (used in excursion generation)
export const EXCURSION_ROOT_CAUSES = [
  { id: 'CARRIER_DELAY', name: 'Carrier Delay', frequency: 0.30 },
  { id: 'EQUIPMENT_FAIL', name: 'Equipment Failure', frequency: 0.25 },
  { id: 'HANDLING_ERROR', name: 'Handling Error', frequency: 0.20 },
  { id: 'WEATHER', name: 'Weather Event', frequency: 0.12 },
  { id: 'CUSTOMS', name: 'Customs Hold', frequency: 0.08 },
  { id: 'UNKNOWN', name: 'Unknown', frequency: 0.05 },
];

// Helper functions
export const getCarrierById = (carrierId) => {
  return CARRIERS.find(c => c.id === carrierId);
};

export const getRandomCarrier = () => {
  return CARRIERS[Math.floor(Math.random() * CARRIERS.length)];
};

export const getRandomOrigin = () => {
  return ORIGINS[Math.floor(Math.random() * ORIGINS.length)];
};

export const getRandomDestination = () => {
  return DESTINATIONS[Math.floor(Math.random() * DESTINATIONS.length)];
};

// Weighted random selection for root causes (more realistic)
export const getRandomRootCause = () => {
  const rand = Math.random();
  let cumulative = 0;
  
  for (const cause of EXCURSION_ROOT_CAUSES) {
    cumulative += cause.frequency;
    if (rand <= cumulative) {
      return cause;
    }
  }
  return EXCURSION_ROOT_CAUSES[EXCURSION_ROOT_CAUSES.length - 1];
};